/**
 * Scenario shape + bootstrap (v0M3.6).
 *
 * A `Scenario` is the configuration emitted by the start-menu Standard-
 * game screen (v0M3.7). `bootstrapScenario` consumes it plus a
 * `ScenarioMapData` and returns a fully-populated `GameState` ready for
 * the first `runTick` call — the sim/client seam that turns "user
 * clicked Start" into "sim is running".
 *
 * Design locks (Michael, 2026-07-12, v0M3.6 elicitation):
 *
 * 1. **6 slots per Scenario**, one per v1 faction (§6.1 #21). Each slot
 *    has a `role`: 'player' | 'ai' | 'neutral'. Exactly one slot must be
 *    Player; validation throws on 0 or >1.
 * 2. **Neutral semantics** (per starting mode):
 *    - Single Province: neutrals exist as separate factions with 1
 *      province + 1 capital. teamId = NEUTRAL_TEAM_ID (-1). No armies.
 *    - Country Divided / United: neutrals do NOT exist in the game.
 *      Every province is assigned to a Player or AI faction.
 * 3. **Capital city per faction** = the city with the largest `pop`
 *    across all owned provinces (major + minor). Deterministic per seed
 *    (stable arg-max by pop, ties broken by lower cell.x then cell.y).
 * 4. **Starting army** for Player/AI factions: 1 Army with 3 Swordsman
 *    cohorts, placed at the capital city's cell. 1 Default Commander
 *    per §2.7.8.7 (level 1 = 6 cohort limit).
 * 5. **Starting resources**: fixed pack — 5000 Gold, 5000 Food, 500 Iron,
 *    500 Wood, 500 Horses, 500 Supplies, 0 Learning. Balance.xlsx dial
 *    candidate — first-playtest tunable.
 * 6. **Difficulty + Victory** captured in the Scenario but not yet
 *    consumed at v0M3.6. `AI` module (v0M3.8) reads difficulty; `victory`
 *    module (v0M3.9) reads victoryCondition.
 *
 * Starting-mode implementations (see corresponding functions below):
 * - Single Province: pick 6 provinces deterministically with min-distance
 *   spread (BFS-based province distance over adjacency graph).
 * - Country Divided: for each non-wilderness province, RNG-pick one of
 *   the active (non-neutral) factions in the slot list. Not necessarily
 *   contiguous — checkerboard-friendly.
 * - Country United: seed placement (min-distance spread) + BFS region
 *   grow over province adjacency graph until every province claimed.
 */

import { getFactionByName } from '../data/factions';
import type { FactionDef } from '../data/factions';
import { loadCohorts } from '../data/cohorts';
import { loadDials, getDialNumber } from '../data/dials';
import type { ScenarioMapData, ScenarioProvince } from './map-data';
import {
  createExploredVisibilityMap,
  createUnexploredVisibilityMap,
} from './visibility';
import { createRng, nextInt } from './rng';
import type { RngState } from './rng';
import {
  createArmy,
  createCommander,
  createEmptyResources,
  createEmptyState,
  sortedIds,
  toArmyId,
  toSettlementId,
  toCohortId,
  toCommanderId,
  toFactionId,
  toProvinceId,
} from './state';
import type {
  Army,
  Settlement,
  Cohort,
  Commander,
  Faction,
  FactionId,
  GameState,
  Province,
  Resources,
} from './state';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type SlotRole = 'player' | 'ai' | 'neutral';
export type Difficulty = 'easy' | 'normal' | 'hard';
export type VictoryCondition = 'destroy-capitals' | 'destroy-cities';
export type StartingMode = 'single-province' | 'country-divided' | 'country-united';

/** Sentinel teamId marking factions that don't belong to any player/AI team. */
export const NEUTRAL_TEAM_ID = -1;

export interface SlotConfig {
  readonly factionName: string;
  readonly role: SlotRole;
}

export interface Scenario {
  readonly mapName: string;
  readonly seed: number;
  readonly slots: readonly SlotConfig[];
  readonly difficulty: Difficulty;
  readonly victoryCondition: VictoryCondition;
  readonly startingMode: StartingMode;
  /**
   * v0M5.2 (2026-07-25) — Initial fog-of-war state per `MAP.md` §7.2.
   * `false` (default): every cell starts at state 1 (Unexplored, black).
   * `true`: every cell starts at state 2 (Explored, terrain visible, no
   * live enemy entities). The player still gains state 3 (visible) via
   * vision propagation each tick — this toggle only seeds the starting
   * "explored" bit. Optional so retro-compat with pre-v0M5.2 scenarios;
   * `bootstrapScenario` treats missing as `false`.
   */
  readonly startExplored?: boolean;
}

// ---------------------------------------------------------------------------
// Fixed dial-candidates — first-playtest tunable (Balance.xlsx follow-up)
// ---------------------------------------------------------------------------

/** Starting resource pack for every Player + AI faction. Neutrals get zero. */
const STARTING_RESOURCES_ACTIVE: Resources = {
  gold: 5000,
  iron: 500,
  wood: 500,
  horses: 500,
  supplies: 5000,
  learning: 0,
};

const STARTING_COHORT_TYPE = 'Swordsman';
const STARTING_COHORT_COUNT = 3;
// Session-28c (2026-07-24): retired the hardcoded `STARTING_PROVINCE_POPULATION = 5000`
// constant. Population is now rolled per province from the xlsx-authoritative
// `province_starting_pop_min` / `province_starting_pop_max` dials (see
// `rollStartingPopulation` below). At the current dial values (10000 / 80000),
// Town-tier capitals have manpower caps of ~1,000-8,000 — enough to recruit
// real armies. Was 500 at the old 50,000 min / 0.01 cap, which was itself
// unused because the hardcoded 5,000 was 10× smaller than the dial range.
const STARTING_PROVINCE_TIER = 'Town' as const;

// v0M4.2.1 — every Settlement spawns with garrison_starting_count Garrison cohorts
// at world-gen per BATTLES.md §3.2. Loaded once at module scope; xlsx-tunable.
const SCENARIO_DIALS = loadDials();
const GARRISON_STARTING_COUNT = Math.max(0, Math.floor(getDialNumber(SCENARIO_DIALS, 'garrison_starting_count')));
const GARRISON_COHORT_TYPE = 'Garrison';

// Session-28c (2026-07-24) — starting-population range dials.
const STARTING_POP_MIN = Math.max(0, Math.floor(getDialNumber(SCENARIO_DIALS, 'province_starting_pop_min')));
const STARTING_POP_MAX = Math.max(STARTING_POP_MIN, Math.floor(getDialNumber(SCENARIO_DIALS, 'province_starting_pop_max')));
const MANPOWER_CAP_FRACTION = getDialNumber(SCENARIO_DIALS, 'manpower_cap_fraction_of_pop');

/**
 * Roll a starting population for a province in [MIN, MAX] inclusive.
 * Uses the scenario rng for determinism (same seed → same populations
 * across hosts, load/save-safe).
 */
function rollStartingPopulation(rng: RngState): number {
  // nextInt is half-open [min, maxExclusive), so pass MAX+1 to include MAX.
  return nextInt(rng, STARTING_POP_MIN, STARTING_POP_MAX + 1);
}
// MFix5+fix4 (2026-07-23) — garrison count seeded into every unassigned
// major city (all cities not owned by a slot faction) at scenario boot.
// Prevents immediate surrender-on-contact and makes neutral cities
// siege-worthy per Michael's playtest ask.
const NEUTRAL_SETTLEMENT_STARTING_GARRISON_COUNT = Math.max(
  0,
  Math.floor(getDialNumber(SCENARIO_DIALS, 'neutral_settlement_starting_garrison_count')),
);
// Sentinel faction id for the synthetic "wilderness" owner of
// unassigned major cities. Chosen high enough to sit outside the
// FactionDef id space (v1: 1-6 for playable factions) and outside any
// plausible extension. Recorded on state.factions with teamId =
// NEUTRAL_TEAM_ID so sieges.ts treats it as hostile-to-all and no army
// of its team ever spawns (no defender-in-province blocking).
const WILDERNESS_FACTION_ID = 999;

/**
 * Minimum province-graph distance between two Player/AI capitals in
 * Single Province mode. Distance measured in edges over the province
 * adjacency graph. `3` = ≥3 provinces between any two capitals — the
 * map's coarsest "you shouldn't spawn adjacent to your enemy" guarantee.
 * If the map can't satisfy this for the requested slot count, the rule
 * relaxes (see `pickSpreadProvinces`).
 */
const MIN_CAPITAL_GRAPH_DISTANCE = 3;

// ---------------------------------------------------------------------------
// Bootstrap entry point
// ---------------------------------------------------------------------------

/**
 * Turn a Scenario + map into a fresh `GameState`. Validates the slot
 * config, sets up factions, distributes territory per `startingMode`,
 * picks capitals, and spawns starting armies. Deterministic per
 * `scenario.seed`.
 */
export function bootstrapScenario(scenario: Scenario, mapData: ScenarioMapData): GameState {
  validateScenario(scenario);

  const rng = createRng(scenario.seed);
  const state = createEmptyState(scenario.seed, rng);

  // Resolve slot factions ahead of any distribution.
  const resolved = scenario.slots.map((s) => ({
    slot: s,
    faction: getFactionByName(s.factionName),
  }));

  // Create Faction entities. Player + AI go on their own teamId (= id);
  // Neutrals share NEUTRAL_TEAM_ID.
  for (const { slot, faction } of resolved) {
    if (scenario.startingMode !== 'single-province' && slot.role === 'neutral') {
      // Skip neutral slots in Divided / United per §2 lock.
      continue;
    }
    const teamId = slot.role === 'neutral' ? NEUTRAL_TEAM_ID : faction.id;
    const startingResources: Resources =
      slot.role === 'neutral' ? createEmptyResources() : { ...STARTING_RESOURCES_ACTIVE };
    const factionEntity: Faction = {
      id: toFactionId(faction.id),
      teamId,
      isAI: slot.role === 'ai',
      resources: startingResources,
      // v0M9.3 (2026-07-24) — start every faction with an empty research
      // ledger + no active research. Neutral factions carry the same
      // (never researches, but keeps state shape uniform).
      researchedKnowledge: [],
      activeResearch: null,
    };
    state.factions.set(factionEntity.id, factionEntity);
  }

  // Distribute provinces per starting mode.
  const distribution = distributeProvinces(scenario, resolved, mapData, rng);

  // Populate Province entities from the distribution.
  populateProvinces(state, distribution, rng);

  // Pick + create Settlement entities. Capital = arg-max pop city per faction.
  const capitals = assignCapitals(state, distribution, mapData);

  // MFix5+fix3 (2026-07-23): materialize the remaining ~90 major cities
  // on the map that are NOT the capital of a slot faction. These become
  // owner-less Settlement entities (province.ownerFactionId = null) with no
  // garrison — they surrender on ZoC contact per MFix5.2, giving the
  // player minor-city sweep gameplay. Without this, the sim's
  // `state.settlements` only knew about the 6 slot capitals, and every other
  // city drawn from static map data was unattackable.
  materializeUnassignedMajorSettlements(state, distribution, mapData, rng);

  // v0M9 (2026-07-27) — materialize every minor settlement as a Settlement
  // entity so the sim can reason about them (pillage / repair / structure
  // hosting). Owner always follows containing Province — no independent
  // ownership. Depends on majors being materialized first so every province
  // referenced here already has a Province entity.
  materializeMinorSettlements(state, mapData);

  // Spawn starting armies at capital cells for Player/AI factions only.
  spawnStartingArmies(state, resolved, capitals);

  // v0M5.2 (2026-07-25) — Seed per-faction visibility maps per MAP.md §7.2.
  // scenario.startExplored=true seeds every cell as state 2 (explored,
  // terrain visible). Default false = every cell state 1 (unexplored,
  // black). The visibility system lazy-creates maps for factions not
  // seeded here, so neutral factions land in a consistent state without
  // per-branch bookkeeping.
  seedFactionVisibility(state, mapData.width, mapData.height, scenario.startExplored === true);

  return state;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function validateScenario(scenario: Scenario): void {
  const players = scenario.slots.filter((s) => s.role === 'player');
  if (players.length === 0) {
    throw new Error('Scenario has no Player slot — exactly one required.');
  }
  if (players.length > 1) {
    throw new Error(`Scenario has ${players.length} Player slots — exactly one required.`);
  }

  // Duplicate factionName check.
  const seen = new Set<string>();
  for (const s of scenario.slots) {
    if (seen.has(s.factionName)) {
      throw new Error(`Duplicate factionName in slots: ${s.factionName}`);
    }
    seen.add(s.factionName);
  }
  // Every factionName must resolve.
  for (const s of scenario.slots) {
    getFactionByName(s.factionName); // throws on unknown
  }
}

// ---------------------------------------------------------------------------
// Distribution
// ---------------------------------------------------------------------------

/** Per-faction province-id set produced by a distribution algorithm. */
type Distribution = Map<FactionId, Set<number>>;

function distributeProvinces(
  scenario: Scenario,
  resolved: readonly { slot: SlotConfig; faction: FactionDef }[],
  mapData: ScenarioMapData,
  rng: RngState,
): Distribution {
  switch (scenario.startingMode) {
    case 'single-province':
      return distributeSingleProvince(resolved, mapData, rng);
    case 'country-divided':
      return distributeDivided(resolved, mapData, rng);
    case 'country-united':
      return distributeUnited(resolved, mapData, rng);
  }
}

/**
 * Single Province: one province per active (non-null role) slot,
 * chosen deterministically with a min-graph-distance spread.
 */
function distributeSingleProvince(
  resolved: readonly { slot: SlotConfig; faction: FactionDef }[],
  mapData: ScenarioMapData,
  rng: RngState,
): Distribution {
  const dist: Distribution = new Map();
  const validProvinces = provincesWithCities(mapData);
  const adjacency = buildProvinceAdjacency(mapData);
  const picks = pickSpreadProvinces(validProvinces, adjacency, resolved.length, rng);

  for (let i = 0; i < resolved.length; i++) {
    const pick = picks[i];
    if (pick === undefined) break; // Map too small for this many slots — silent trim.
    const factionId = toFactionId(resolved[i]!.faction.id);
    dist.set(factionId, new Set([pick]));
  }
  return dist;
}

/**
 * Country Divided: for every valid province, RNG-pick one of the active
 * (non-neutral) slots. Deterministic per seed.
 */
function distributeDivided(
  resolved: readonly { slot: SlotConfig; faction: FactionDef }[],
  mapData: ScenarioMapData,
  rng: RngState,
): Distribution {
  const active = resolved.filter((r) => r.slot.role !== 'neutral');
  const dist: Distribution = new Map();
  for (const a of active) dist.set(toFactionId(a.faction.id), new Set());
  if (active.length === 0) return dist;

  const validProvinces = provincesWithCities(mapData);
  // Deterministic province iteration: sort ids before RNG assignment.
  const sortedPids = [...validProvinces].sort((a, b) => a - b);
  for (const pid of sortedPids) {
    const idx = nextInt(rng, 0, active.length);
    const pick = active[idx]!;
    dist.get(toFactionId(pick.faction.id))!.add(pid);
  }
  return dist;
}

/**
 * Country United: seed placement (min-distance spread) + BFS region grow
 * over province adjacency graph until every province claimed. Growth
 * order is round-robin across seeds so no seed dominates.
 */
function distributeUnited(
  resolved: readonly { slot: SlotConfig; faction: FactionDef }[],
  mapData: ScenarioMapData,
  rng: RngState,
): Distribution {
  const active = resolved.filter((r) => r.slot.role !== 'neutral');
  const dist: Distribution = new Map();
  for (const a of active) dist.set(toFactionId(a.faction.id), new Set());
  if (active.length === 0) return dist;

  const validProvinces = provincesWithCities(mapData);
  const adjacency = buildProvinceAdjacency(mapData);
  const seeds = pickSpreadProvinces(validProvinces, adjacency, active.length, rng);

  // Assign seed provinces.
  const claimed = new Map<number, FactionId>(); // provinceId → factionId
  for (let i = 0; i < seeds.length; i++) {
    const seed = seeds[i]!;
    const factionId = toFactionId(active[i]!.faction.id);
    dist.get(factionId)!.add(seed);
    claimed.set(seed, factionId);
  }

  // Level-BFS growth: each round-robin turn, each faction claims AT MOST
  // ONE new adjacent province. This gives symmetric Voronoi-like regions;
  // a naive all-neighbours-at-once claim lets the first-iterated faction
  // grab all adjacent provinces before the next faction's turn.
  const frontiers: Map<FactionId, number[]> = new Map();
  for (let i = 0; i < seeds.length; i++) {
    const seed = seeds[i]!;
    const factionId = toFactionId(active[i]!.faction.id);
    frontiers.set(factionId, [seed]);
  }

  let progress = true;
  while (progress) {
    progress = false;
    for (const [factionId, frontier] of frontiers) {
      if (frontier.length === 0) continue;
      const current = frontier.shift()!;
      // Deterministic neighbour iteration by sorted province id.
      const neighbors = [...(adjacency.get(current) ?? [])].sort((a, b) => a - b);
      // Claim AT MOST one unclaimed neighbour this turn.
      let claimedOne = false;
      for (const n of neighbors) {
        if (!claimed.has(n) && validProvinces.has(n)) {
          claimed.set(n, factionId);
          dist.get(factionId)!.add(n);
          frontier.push(n);
          claimedOne = true;
          progress = true;
          break;
        }
      }
      if (claimedOne) {
        // Re-queue `current` at the head so we revisit for its remaining
        // unclaimed neighbours next round-robin cycle.
        frontier.unshift(current);
      }
    }
  }

  return dist;
}

// ---------------------------------------------------------------------------
// Province / capital population helpers
// ---------------------------------------------------------------------------

function populateProvinces(state: GameState, distribution: Distribution, rng: RngState): void {
  for (const [factionId, provinceIds] of distribution) {
    for (const pid of provinceIds) {
      const province: Province = {
        id: toProvinceId(pid),
        ownerFactionId: factionId,
        // Session-28c (2026-07-24): roll per-province from xlsx dials
        // (min=10000, max=80000 currently). Deterministic via scenario rng.
        population: rollStartingPopulation(rng),
        tier: STARTING_PROVINCE_TIER,
        structures: [],
        buildQueue: [],
        recruitQueue: [],
      };
      state.provinces.set(province.id, province);
    }
  }
}

/**
 * For each faction, pick the largest-pop city across all owned provinces
 * as its capital. Creates a Settlement entity at that location, records the
 * capital settlementId back onto the Faction entity (for the v0M3.9 victory
 * check), and returns a map of factionId → capital cell for army spawn.
 */
function assignCapitals(
  state: GameState,
  distribution: Distribution,
  mapData: ScenarioMapData,
): Map<FactionId, { x: number; y: number }> {
  const capitals = new Map<FactionId, { x: number; y: number }>();
  let settlementCounter = 1;

  for (const [factionId, provinceIds] of distribution) {
    let bestPop = -1;
    let bestCell: { x: number; y: number } | null = null;
    let bestProvinceId: number | null = null;

    for (const pid of provinceIds) {
      const meta = findProvince(mapData, pid);
      if (!meta) continue;
      const cities = allCitiesOf(meta);
      for (const c of cities) {
        // Tie-break: higher pop wins; ties → lower x then lower y.
        const [cx, cy] = c.cell;
        if (
          c.pop > bestPop ||
          (c.pop === bestPop && bestCell !== null &&
            (cx < bestCell.x || (cx === bestCell.x && cy < bestCell.y)))
        ) {
          bestPop = c.pop;
          bestCell = { x: cx, y: cy };
          bestProvinceId = pid;
        }
      }
    }

    if (bestCell !== null && bestProvinceId !== null) {
      const settlementId = toSettlementId(settlementCounter++);
      // Session-28c: seed manpower from the province's actual rolled
      // population (populateProvinces has already run above), not the
      // retired STARTING_PROVINCE_POPULATION constant.
      const provinceEntity = state.provinces.get(toProvinceId(bestProvinceId));
      const provincePop = provinceEntity?.population ?? STARTING_POP_MIN;
      const city: Settlement = {
        id: settlementId,
        provinceId: toProvinceId(bestProvinceId),
        // v0M3.11: persist the city cell so economy.spawnCohort can spawn
        // newly recruited armies here (was hardcoded (0, 0) pre-v0M3.11).
        cell: { x: bestCell.x, y: bestCell.y },
        // v0M4.2.1: BATTLES.md §3.2 — city-attached garrison roster.
        // Populated by seedStartingGarrison() below.
        garrisonCohortIds: [],
        // v0M4.2.2: BATTLES.md §3 — no active siege at world-gen.
        siegeState: null,
        // MFix6.6.1 (2026-07-23) — starting manpower pool. Seeded at
        // the cap (population × manpower_cap_fraction_of_pop) so faction
        // capitals can recruit immediately without waiting for regen.
        // Session-28c: cap is 10% of pop (was 1%) — Town-tier capitals
        // start with 1,000-8,000 manpower, enough for real armies.
        manpower: Math.floor(provincePop * MANPOWER_CAP_FRACTION),
        manpowerRace: 'Human',
      };
      state.settlements.set(settlementId, city);
      capitals.set(factionId, bestCell);
      // Record capital on the Faction (v0M3.9). Replace the entity in the
      // map because `capitalSettlementId` is declared readonly.
      const existing = state.factions.get(factionId);
      if (existing) {
        state.factions.set(factionId, { ...existing, capitalSettlementId: settlementId });
      }
      // v0M4.2.1: seed the starting garrison for this city per BATTLES.md §3.2.
      seedStartingGarrison(state, settlementId, factionId);
    }
  }

  return capitals;
}

/**
 * v0M4.2.1 — seed `GARRISON_STARTING_COUNT` Garrison cohorts into the
 * given city at world-gen per BATTLES.md §3.2. Garrison cohorts have
 * `armyId: null, settlementId: settlementId` and their id is pushed onto the city's
 * `garrisonCohortIds`. Stats come from the Cohorts sheet 'Garrison' row.
 * Silent no-op if the Garrison row is missing (v1 always ships it).
 *
 * Called from `assignCapitals` once per created Settlement. Follow-up: when
 * minor cities are materialized as `Settlement` entities (currently only major
 * capitals become Cities in the state map), this helper wants to be
 * called for them too.
 */
function seedStartingGarrison(
  state: GameState,
  settlementId: import('./state').SettlementId,
  factionId: FactionId,
): void {
  if (GARRISON_STARTING_COUNT <= 0) return;
  const COHORTS = loadCohorts();
  const spec = COHORTS[GARRISON_COHORT_TYPE];
  if (!spec) return; // Garrison row missing from xlsx — silent skip (dev-only case).
  const city = state.settlements.get(settlementId);
  if (!city) return;

  for (let i = 0; i < GARRISON_STARTING_COUNT; i++) {
    const cohortId = toCohortId(state.nextCohortId++);
    const garrison: Cohort = {
      id: cohortId,
      factionId,
      armyId: null,
      settlementId,
      type: GARRISON_COHORT_TYPE,
      aliveFigureCount: spec.figureCount,
      stamina: 100,
      morale: 100,
      xp: 0,
    };
    state.cohorts.set(cohortId, garrison);
    city.garrisonCohortIds.push(cohortId);
  }
}

// ---------------------------------------------------------------------------
// Army spawn (Player/AI only)
// ---------------------------------------------------------------------------

function spawnStartingArmies(
  state: GameState,
  resolved: readonly { slot: SlotConfig; faction: FactionDef }[],
  capitals: ReadonlyMap<FactionId, { x: number; y: number }>,
): void {
  const COHORTS = loadCohorts();
  const spec = COHORTS[STARTING_COHORT_TYPE];
  if (!spec) {
    throw new Error(`Starting cohort type '${STARTING_COHORT_TYPE}' not in Balance.xlsx Cohorts.`);
  }

  for (const { slot, faction } of resolved) {
    if (slot.role === 'neutral') continue;

    const factionId = toFactionId(faction.id);
    const capital = capitals.get(factionId);
    if (!capital) continue; // Faction has no territory — no army spawn.

    // Commander first.
    const commanderId = toCommanderId(state.nextCommanderId++);
    const commander: Commander = createCommander(commanderId);
    state.commanders.set(commanderId, commander);

    // Army at capital cell (centre of the cell as float position).
    const armyId = toArmyId(state.nextArmyId++);
    const army: Army = createArmy(
      armyId,
      factionId,
      commanderId,
      { x: capital.x + 0.5, y: capital.y + 0.5 },
    );

    // STARTING_COHORT_COUNT cohorts attached to the army.
    for (let i = 0; i < STARTING_COHORT_COUNT; i++) {
      const cohortId = toCohortId(state.nextCohortId++);
      const cohort: Cohort = {
        id: cohortId,
        factionId,
        armyId,
        settlementId: null,
        type: STARTING_COHORT_TYPE,
        aliveFigureCount: spec.figureCount,
        stamina: 100,
        morale: 100,
        xp: 0,
      };
      state.cohorts.set(cohortId, cohort);
      army.cohortIds.push(cohortId);
    }

    state.armies.set(armyId, army);
  }
}

// ---------------------------------------------------------------------------
// Adjacency + spread helpers (shared across modes)
// ---------------------------------------------------------------------------

/**
 * Build a province adjacency graph — pid → set of neighbouring pids. Two
 * provinces are neighbours if they share at least one 4-cardinally
 * adjacent cell pair on `provinceMap`. Wilderness (id 0) is skipped.
 */
function buildProvinceAdjacency(mapData: ScenarioMapData): Map<number, Set<number>> {
  const adj = new Map<number, Set<number>>();
  const w = mapData.width;
  const h = mapData.height;
  const pmap = mapData.provinceMap;

  const link = (a: number, b: number) => {
    if (a === b || a === 0 || b === 0) return;
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a)!.add(b);
    adj.get(b)!.add(a);
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const here = pmap[y * w + x]!;
      if (x + 1 < w) link(here, pmap[y * w + (x + 1)]!);
      if (y + 1 < h) link(here, pmap[(y + 1) * w + x]!);
    }
  }
  return adj;
}

/**
 * Pick N provinces spread across the map using a greedy min-graph-distance
 * heuristic. Starts with a deterministic seed pick, then adds provinces
 * whose min distance to any already-picked is maximised. Falls back to
 * random selection if fewer valid provinces than N.
 */
function pickSpreadProvinces(
  valid: Set<number>,
  adjacency: Map<number, Set<number>>,
  n: number,
  rng: RngState,
): number[] {
  if (n <= 0) return [];
  const validSorted = [...valid].sort((a, b) => a - b); // determinism
  if (validSorted.length === 0) return [];

  // Seed: RNG pick from valid.
  const seedIdx = nextInt(rng, 0, validSorted.length);
  const picks: number[] = [validSorted[seedIdx]!];

  while (picks.length < n && picks.length < validSorted.length) {
    let best: number | null = null;
    let bestDist = -1;
    for (const cand of validSorted) {
      if (picks.includes(cand)) continue;
      // Distance = min BFS distance from cand to any pick.
      let minToPicks = Infinity;
      for (const p of picks) {
        const d = provinceGraphDistance(adjacency, cand, p, MIN_CAPITAL_GRAPH_DISTANCE + 2);
        if (d < minToPicks) minToPicks = d;
      }
      if (minToPicks > bestDist) {
        bestDist = minToPicks;
        best = cand;
      }
    }
    if (best === null) break;
    picks.push(best);
  }
  return picks;
}

/**
 * BFS distance between two provinces over the adjacency graph. Returns
 * Infinity if unreachable within `limit` edges (short-circuit for perf).
 */
function provinceGraphDistance(
  adjacency: Map<number, Set<number>>,
  a: number,
  b: number,
  limit: number,
): number {
  if (a === b) return 0;
  const visited = new Set<number>([a]);
  let frontier: number[] = [a];
  let depth = 0;
  while (frontier.length > 0 && depth < limit) {
    const next: number[] = [];
    depth++;
    for (const n of frontier) {
      for (const m of adjacency.get(n) ?? []) {
        if (m === b) return depth;
        if (!visited.has(m)) {
          visited.add(m);
          next.push(m);
        }
      }
    }
    frontier = next;
  }
  return Infinity;
}

/** Return the set of province ids that have at least one city (major or minor). */
function provincesWithCities(mapData: ScenarioMapData): Set<number> {
  const set = new Set<number>();
  for (const p of mapData.provinces) {
    if (p.majorCity || (p.minorCities && p.minorCities.length > 0)) {
      set.add(p.id);
    }
  }
  return set;
}

function findProvince(mapData: ScenarioMapData, pid: number): ScenarioProvince | undefined {
  return mapData.provinces.find((p) => p.id === pid);
}

function allCitiesOf(meta: ScenarioProvince): { readonly cell: readonly [number, number]; readonly pop: number }[] {
  const out: { cell: readonly [number, number]; pop: number }[] = [];
  if (meta.majorCity) out.push({ cell: meta.majorCity.cell, pop: meta.majorCity.pop });
  for (const m of meta.minorCities ?? []) out.push({ cell: m.cell, pop: m.pop });
  return out;
}

/**
 * MFix5+fix3 (2026-07-23) — Materialize every major city on the map
 * (~100 total across all provinces) as a `Settlement` entity in `state.settlements`,
 * even if the containing province wasn't assigned to any slot faction.
 *
 * Why: pre-MFix5+fix3, `state.settlements` only held the 6 slot-capital
 * cities picked by `assignCapitals`. The other ~94 major cities on the
 * map were painted from static map data by the renderer but the sim
 * never saw them — so `sieges.ts` never triggered on them and the
 * player couldn't attack them.
 *
 * For each unassigned province, this creates:
 *  - A `Province` entity with `ownerFactionId = null` (neutral wilderness).
 *  - A `Settlement` entity for its major city with empty `garrisonCohortIds`.
 *
 * Result: player entering an unassigned city's ZoC gets an immediate
 * surrender per MFix5.2 (no defender field army, no garrison). Provinces
 * assigned to slot factions already have their major cities materialized
 * by `assignCapitals` — we skip them here.
 *
 * Minor cities remain map-data-only. Post-MVP milestone tracks their
 * materialization for garrison + minor-siege gameplay.
 *
 * Population + tier defaults match `populateProvinces` so downstream
 * economy code sees a valid Province shape.
 */
function materializeUnassignedMajorSettlements(
  state: GameState,
  distribution: Distribution,
  mapData: ScenarioMapData,
  rng: RngState,
): void {
  // Build a set of assigned province ids for O(1) skip check.
  const assignedProvinceIds = new Set<number>();
  for (const provinceIds of distribution.values()) {
    for (const pid of provinceIds) assignedProvinceIds.add(pid);
  }

  // Settlement-id counter continues from the highest existing id (assignCapitals
  // used 1..N; keep going from there so ids stay unique + deterministic).
  let settlementCounter = 1;
  for (const cid of state.settlements.keys()) {
    const n = cid as unknown as number;
    if (n >= settlementCounter) settlementCounter = n + 1;
  }

  // MFix5+fix5 (2026-07-23): synthetic wilderness faction owns every
  // unassigned major city + hosts their garrisons. teamId =
  // NEUTRAL_TEAM_ID so sieges.ts treats it as hostile-to-all (any
  // non-wilderness army entering ZoC opens a siege) and no defender-
  // in-province check ever finds a wilderness army (they don't spawn).
  // Only created if at least one unassigned city exists AND we're
  // seeding garrisons (otherwise leave ownerFactionId = null for the
  // ancient v1 "unowned" semantics).
  const seedGarrisons = NEUTRAL_SETTLEMENT_STARTING_GARRISON_COUNT > 0;
  const wildernessFactionId = toFactionId(WILDERNESS_FACTION_ID);
  let wildernessFactionCreated = false;
  const ensureWildernessFaction = (): void => {
    if (wildernessFactionCreated || !seedGarrisons) return;
    if (!state.factions.has(wildernessFactionId)) {
      state.factions.set(wildernessFactionId, {
        id: wildernessFactionId,
        teamId: NEUTRAL_TEAM_ID,
        isAI: false,
        resources: createEmptyResources(),
      });
    }
    wildernessFactionCreated = true;
  };

  // Cohort spec lookup — cached because seedGarrisons may hit this many
  // times (one wilderness faction × ~94 unassigned cities × N garrisons).
  const COHORTS = loadCohorts();
  const garrisonSpec = COHORTS[GARRISON_COHORT_TYPE];

  for (const provinceMeta of mapData.provinces) {
    if (assignedProvinceIds.has(provinceMeta.id)) continue;
    if (!provinceMeta.majorCity) continue; // Wilderness province — no major city.

    // Create the Province entity. Owner = wilderness faction (if
    // garrisons are seeded) or null (if not — pre-MFix5+fix5 behavior).
    const provinceId = toProvinceId(provinceMeta.id);
    const provinceOwner = seedGarrisons ? wildernessFactionId : null;
    // Session-28c (2026-07-24): roll pop per province (deterministic via rng).
    // Compute BEFORE the province-create branch so we can use the same
    // value for city.manpower even if the province already exists.
    const rolledPop = rollStartingPopulation(rng);
    if (!state.provinces.has(provinceId)) {
      const province: Province = {
        id: provinceId,
        ownerFactionId: provinceOwner,
        population: rolledPop,
        tier: STARTING_PROVINCE_TIER,
        structures: [],
        buildQueue: [],
        recruitQueue: [],
      };
      state.provinces.set(provinceId, province);
    }
    const provincePop = state.provinces.get(provinceId)!.population;

    // Create the major-city Settlement entity.
    const settlementId = toSettlementId(settlementCounter++);
    const [cx, cy] = provinceMeta.majorCity.cell;
    const city: Settlement = {
      id: settlementId,
      provinceId,
      cell: { x: cx, y: cy },
      garrisonCohortIds: [],
      siegeState: null,
      // MFix6.6.1 (2026-07-23): wilderness / unassigned cities also get
      // a manpower pool seeded at cap. Neutral / conquered cities can
      // recruit as soon as a player captures them.
      // Session-28c: seed from actual province pop (not the retired
      // STARTING_PROVINCE_POPULATION constant).
      manpower: Math.floor(provincePop * MANPOWER_CAP_FRACTION),
      manpowerRace: 'Human',
    };
    state.settlements.set(settlementId, city);

    // Seed garrisons per MFix5+fix5 dial. Only fires when the dial is
    // > 0 AND the Garrison cohort spec is loadable (silent skip if the
    // xlsx row is missing — dev-only edge case).
    if (seedGarrisons && garrisonSpec !== undefined) {
      ensureWildernessFaction();
      for (let i = 0; i < NEUTRAL_SETTLEMENT_STARTING_GARRISON_COUNT; i++) {
        const cohortId = toCohortId(state.nextCohortId++);
        const garrison: Cohort = {
          id: cohortId,
          factionId: wildernessFactionId,
          armyId: null,
          settlementId,
          type: GARRISON_COHORT_TYPE,
          aliveFigureCount: garrisonSpec.figureCount,
          stamina: 100,
          morale: 100,
          xp: 0,
        };
        state.cohorts.set(cohortId, garrison);
        city.garrisonCohortIds.push(cohortId);
      }
    }
  }
}

/**
 * v0M9 (2026-07-27) — Materialize every minor settlement on the map
 * (`province.minorCities[]`) as a `Settlement` entity in `state.settlements`.
 *
 * Called AFTER `materializeUnassignedMajorSettlements` so every province
 * already has its Province entity + Province Capital Settlement. Minors
 * are always owned by their province owner (no independent ownership
 * per MECHANICS.md §3 v0M9 amendment); ownership is resolved on demand
 * via `province.ownerFactionId` lookup — no field on Settlement.
 *
 * Starting garrison: 0 (Michael's spec — "Timer to Pillage"). Any
 * enemy that enters the minor's ZoC with no garrison present starts the
 * pillage timer immediately per `pillages.ts`; the province owner may
 * later recruit a garrison via the settlement panel (post-v0M9 UI
 * follow-up if not shipped in this milestone).
 *
 * Manpower: 0 (minors don't recruit — Armies are created at Province
 * Capitals per MECHANICS §3.1). Preserves the schema shape without
 * misleading the Recruit tab, which is capital-only.
 */
function materializeMinorSettlements(
  state: GameState,
  mapData: ScenarioMapData,
): void {
  // Continue the settlement-id counter from wherever the major
  // materialization left off, so ids stay unique + deterministic.
  let settlementCounter = 1;
  for (const cid of state.settlements.keys()) {
    const n = cid as unknown as number;
    if (n >= settlementCounter) settlementCounter = n + 1;
  }

  for (const provinceMeta of mapData.provinces) {
    const minors = provinceMeta.minorCities;
    if (!minors || minors.length === 0) continue;
    const provinceId = toProvinceId(provinceMeta.id);
    // Skip provinces without a materialized Province entity (defensive —
    // materializeUnassignedMajorSettlements should have covered all
    // provinces that carry a majorCity).
    if (!state.provinces.has(provinceId)) continue;

    for (const minor of minors) {
      const settlementId = toSettlementId(settlementCounter++);
      const [cx, cy] = minor.cell;
      const minorSettlement: Settlement = {
        id: settlementId,
        provinceId,
        cell: { x: cx, y: cy },
        garrisonCohortIds: [],
        siegeState: null,
        // v0M9 flag + flavor kind + defaults. Structure cap = 2 per
        // MECHANICS §3 v0M9 amendment. No manpower (Village-tier +
        // minors don't recruit).
        isMinor: true,
        kind: minor.kind,
        structureCap: 2,
        pillaged: false,
        pillageState: null,
        repairState: null,
        manpower: 0,
        manpowerRace: 'Human',
      };
      state.settlements.set(settlementId, minorSettlement);
    }
  }
}

/**
 * v0M5.2 (2026-07-25) — Seed a `VisibilityMap` per non-neutral faction
 * per MAP.md §7.2 New Game option. `startExplored=false` (default)
 * seeds state 1 (all-black); `true` seeds state 2 (terrain visible, no
 * live enemies). The visibility system's first tick will overwrite the
 * transient (visible/detected) bits from BFS, but the sticky "explored"
 * bit set here persists — a state-1 seed remains state-1 until the
 * player's vision propagates over the cell.
 */
function seedFactionVisibility(
  state: GameState,
  width: number,
  height: number,
  startExplored: boolean,
): void {
  if (!state.visibility) state.visibility = new Map();
  for (const factionId of sortedIds(state.factions)) {
    const faction = state.factions.get(factionId as FactionId);
    if (!faction || faction.teamId < 0) continue; // Skip neutral / wilderness.
    const map = startExplored
      ? createExploredVisibilityMap(width, height)
      : createUnexploredVisibilityMap(width, height);
    state.visibility.set(faction.id, map);
  }
}

